import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Stack,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Button,
  Tooltip,
  Divider,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningIcon from '@mui/icons-material/Warning';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

type QuestionStatus = 'pending' | 'researching' | 'writing' | 'review' | 'completed' | 'insufficient_info' | 'skipped';

interface Question {
  id: string;
  questionNumber: number;
  questionText: string;
  questionType: string;
  status: QuestionStatus;
  answer?: string;
  answerMetadata?: {
    wordCount: number;
    targetWordCount?: number;
    sourcesCited: string[];
    confidenceScore: number;
  };
  retrievedContext?: Array<{
    documentTitle: string;
    relevanceScore: number;
  }>;
  insufficientInfo?: {
    availableContext: string;
    missingInformation: string[];
    suggestedSources: string[];
  };
  progress?: number;
  currentStep?: string;
}

interface QuestionProgressViewProps {
  questions: Question[];
  onRegenerate: (questionId: string) => void;
  onSkip: (questionId: string) => void;
  onEdit: (questionId: string) => void;
  onProvideContext: (questionId: string) => void;
}

export default function QuestionProgressView({
  questions,
  onRegenerate,
  onSkip,
  onEdit,
  onProvideContext,
}: QuestionProgressViewProps) {
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleExpanded = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: QuestionStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'insufficient_info':
        return <WarningIcon color="warning" />;
      case 'skipped':
        return <SkipNextIcon color="disabled" />;
      case 'pending':
        return <PendingIcon color="action" />;
      case 'researching':
      case 'writing':
        return <AutorenewIcon sx={{ animation: 'spin 2s linear infinite' }} color="primary" />;
      default:
        return <PendingIcon color="action" />;
    }
  };

  const getStatusColor = (status: QuestionStatus): 'default' | 'primary' | 'success' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'insufficient_info':
        return 'warning';
      case 'skipped':
        return 'default';
      case 'researching':
      case 'writing':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: QuestionStatus) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'insufficient_info':
        return 'Needs Info';
      case 'skipped':
        return 'Skipped';
      case 'researching':
        return 'Researching';
      case 'writing':
        return 'Writing';
      case 'review':
        return 'Review';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  const completedCount = questions.filter(q => q.status === 'completed').length;
  const totalProgress = questions.length > 0 ? (completedCount / questions.length) * 100 : 0;

  return (
    <Card elevation={2}>
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Question Progress</Typography>
              <Chip
                label={`${completedCount}/${questions.length}`}
                color={completedCount === questions.length ? 'success' : 'primary'}
                size="small"
              />
            </Stack>

            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {Math.round(totalProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={totalProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Box>

          <Divider />

          <List sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {questions.map((question) => {
              const isExpanded = expandedQuestions.has(question.id);

              return (
                <Card key={question.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ pt: 0.5 }}>{getStatusIcon(question.status)}</Box>

                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              Q{question.questionNumber}
                            </Typography>
                            <Chip
                              label={getStatusLabel(question.status)}
                              color={getStatusColor(question.status)}
                              size="small"
                              sx={{ height: 20 }}
                            />
                            <Chip
                              label={question.questionType}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          </Stack>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {question.questionText}
                          </Typography>

                          {question.currentStep && (
                            <Typography variant="caption" color="text.secondary">
                              {question.currentStep}
                            </Typography>
                          )}

                          {question.progress !== undefined && question.status !== 'completed' && (
                            <LinearProgress
                              variant="determinate"
                              value={question.progress}
                              sx={{ mt: 1, height: 4, borderRadius: 2 }}
                            />
                          )}

                          {question.answerMetadata && (
                            <Stack direction="row" spacing={2} mt={1}>
                              <Typography variant="caption" color="text.secondary">
                                {question.answerMetadata.wordCount} words
                                {question.answerMetadata.targetWordCount &&
                                  ` / ${question.answerMetadata.targetWordCount} target`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Confidence: {Math.round(question.answerMetadata.confidenceScore * 100)}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {question.answerMetadata.sourcesCited.length} sources cited
                              </Typography>
                            </Stack>
                          )}
                        </Box>

                        <Stack direction="row" spacing={0.5}>
                          {question.status === 'insufficient_info' && (
                            <Tooltip title="Provide Context">
                              <IconButton size="small" onClick={() => onProvideContext(question.id)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {(question.status === 'completed' || question.status === 'review') && (
                            <>
                              <Tooltip title="Edit Answer">
                                <IconButton size="small" onClick={() => onEdit(question.id)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Regenerate">
                                <IconButton size="small" onClick={() => onRegenerate(question.id)}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}

                          {question.status === 'pending' && (
                            <Tooltip title="Skip Question">
                              <IconButton size="small" onClick={() => onSkip(question.id)}>
                                <SkipNextIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <IconButton size="small" onClick={() => toggleExpanded(question.id)}>
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Collapse in={isExpanded}>
                        <Box sx={{ mt: 2, pl: 5 }}>
                          {question.insufficientInfo && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              <Typography variant="body2" fontWeight={600} gutterBottom>
                                Insufficient Information
                              </Typography>
                              <Typography variant="caption" display="block" gutterBottom>
                                {question.insufficientInfo.availableContext}
                              </Typography>
                              {question.insufficientInfo.missingInformation.length > 0 && (
                                <>
                                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 1 }}>
                                    Missing:
                                  </Typography>
                                  <List dense disablePadding>
                                    {question.insufficientInfo.missingInformation.map((info, idx) => (
                                      <ListItem key={idx} disablePadding>
                                        <Typography variant="caption">• {info}</Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </>
                              )}
                              {question.insufficientInfo.suggestedSources.length > 0 && (
                                <>
                                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 1 }}>
                                    Suggested sources:
                                  </Typography>
                                  <List dense disablePadding>
                                    {question.insufficientInfo.suggestedSources.map((source, idx) => (
                                      <ListItem key={idx} disablePadding>
                                        <Typography variant="caption">• {source}</Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onProvideContext(question.id)}
                                sx={{ mt: 1 }}
                              >
                                Provide Context
                              </Button>
                            </Alert>
                          )}

                          {question.retrievedContext && question.retrievedContext.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                                Retrieved Context ({question.retrievedContext.length} documents):
                              </Typography>
                              <List dense disablePadding>
                                {question.retrievedContext.slice(0, 5).map((ctx, idx) => (
                                  <ListItem key={idx} disablePadding>
                                    <ListItemText
                                      primary={
                                        <Typography variant="caption">
                                          {ctx.documentTitle} (relevance: {Math.round(ctx.relevanceScore * 100)}%)
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}

                          {question.answer && (
                            <Box>
                              <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                                Answer:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  p: 2,
                                  backgroundColor: 'background.default',
                                  borderRadius: 1,
                                  maxHeight: 300,
                                  overflowY: 'auto',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {question.answer}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </List>
        </Stack>
      </CardContent>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Card>
  );
}
